import React, { useEffect, useState, useRef } from "react";
import { Modal, Form, Button } from "react-bootstrap";
import { toast } from "react-toastify";
import {
  searchProjects,
  fetchReassignmentOptions,
  reassignCivilEngineer,
} from "../api/service";

// F6 — Reallocate Project (civil engineer reassignment)
// Civil_head picks a project, sees current civil engineer, picks a new one,
// confirms; backend moves all open tasks from current → new in one shot.

function ProjectPicker({ value, onChange, placeholder, disabled }) {
  const [query, setQuery] = useState("");
  const [options, setOptions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!query || query.length < 1) {
      setOptions([]);
      return;
    }
    const handle = setTimeout(async () => {
      try {
        setLoading(true);
        const res = await searchProjects(query);
        setOptions(res?.results || []);
      } catch {
        setOptions([]);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(handle);
  }, [query]);

  useEffect(() => {
    function onClickOutside(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  return (
    <div ref={wrapRef} style={{ position: "relative" }}>
      <Form.Control
        type="text"
        placeholder={placeholder || "Type project ID or customer name…"}
        value={value ? value.display || value.name || `Proj ${value.id}` : query}
        disabled={disabled}
        onChange={(e) => {
          if (value) onChange(null);
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
      />
      {open && (query.length > 0 || loading) && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            zIndex: 1000,
            background: "white",
            border: "1px solid #ddd",
            borderRadius: 4,
            maxHeight: 240,
            overflowY: "auto",
            marginTop: 2,
            boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
          }}
        >
          {loading && <div className="text-muted small p-2">Searching…</div>}
          {!loading && options.length === 0 && query.length > 0 && (
            <div className="text-muted small p-2">No matches</div>
          )}
          {!loading &&
            options.map((opt) => (
              <div
                key={opt.id}
                style={{ padding: "6px 10px", cursor: "pointer" }}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  onChange(opt);
                  setQuery("");
                  setOpen(false);
                }}
                className="picker-row"
              >
                {opt.display || opt.name || `Proj ${opt.id}`}
              </div>
            ))}
        </div>
      )}
    </div>
  );
}

export default function ReallocateProjectModal({ show, onHide }) {
  const [selectedProject, setSelectedProject] = useState(null);
  const [currentOwners, setCurrentOwners] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [fromUserId, setFromUserId] = useState("");
  const [toUserId, setToUserId] = useState("");
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Reset everything when modal closes
  useEffect(() => {
    if (!show) {
      setSelectedProject(null);
      setCurrentOwners([]);
      setCandidates([]);
      setFromUserId("");
      setToUserId("");
      setLoadingOptions(false);
      setSubmitting(false);
    }
  }, [show]);

  // When project picked, load reassignment options
  useEffect(() => {
    if (!selectedProject) {
      setCurrentOwners([]);
      setCandidates([]);
      setFromUserId("");
      setToUserId("");
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setLoadingOptions(true);
        const data = await fetchReassignmentOptions(selectedProject.id);
        if (cancelled) return;
        const owners = data?.current_owners || [];
        setCurrentOwners(owners);
        setCandidates(data?.candidates || []);
        // Auto-pick "from" if exactly one current owner
        if (owners.length === 1) {
          setFromUserId(String(owners[0].user_id));
        } else {
          setFromUserId("");
        }
        setToUserId("");
      } catch (err) {
        if (cancelled) return;
        toast.error(
          err?.response?.data?.detail ||
            "Could not load reassignment options for this project"
        );
        setCurrentOwners([]);
        setCandidates([]);
      } finally {
        if (!cancelled) setLoadingOptions(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedProject]);

  async function handleSubmit() {
    if (!selectedProject || !fromUserId || !toUserId) return;
    if (fromUserId === toUserId) {
      toast.error("Current and new civil engineer must be different");
      return;
    }
    try {
      setSubmitting(true);
      const data = await reassignCivilEngineer(
        selectedProject.id,
        parseInt(fromUserId, 10),
        parseInt(toUserId, 10)
      );
      toast.success(
        `Reassigned ${data.tasks_moved} tasks from ${data.from_user.email} to ${data.to_user.email}`
      );
      onHide();
    } catch (err) {
      toast.error(
        err?.response?.data?.detail ||
          "Reassignment failed. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  }

  const canSubmit =
    !!selectedProject &&
    !!fromUserId &&
    !!toUserId &&
    fromUserId !== toUserId &&
    !submitting &&
    !loadingOptions;

  // Filter "to" candidates to exclude the chosen "from"
  const toOptions = candidates.filter(
    (c) => String(c.user_id) !== String(fromUserId)
  );

  return (
    <Modal show={show} onHide={onHide} centered backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>Reallocate Project</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Group className="mb-3">
          <Form.Label>Project</Form.Label>
          <ProjectPicker
            value={selectedProject}
            onChange={setSelectedProject}
            disabled={submitting}
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Current civil engineer</Form.Label>
          {loadingOptions ? (
            <Form.Control type="text" value="Loading…" disabled />
          ) : !selectedProject ? (
            <Form.Control
              type="text"
              value=""
              placeholder="Select a project first"
              disabled
            />
          ) : currentOwners.length === 0 ? (
            <Form.Control
              type="text"
              value="No civil engineer currently assigned to this project"
              disabled
            />
          ) : currentOwners.length === 1 ? (
            <Form.Control
              type="text"
              value={`${currentOwners[0].email} (${currentOwners[0].open_task_count} open tasks)`}
              disabled
            />
          ) : (
            <Form.Select
              value={fromUserId}
              onChange={(e) => setFromUserId(e.target.value)}
              disabled={submitting}
            >
              <option value="">Select current engineer…</option>
              {currentOwners.map((o) => (
                <option key={o.user_id} value={o.user_id}>
                  {o.email} ({o.open_task_count} open tasks)
                </option>
              ))}
            </Form.Select>
          )}
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>New civil engineer</Form.Label>
          <Form.Select
            value={toUserId}
            onChange={(e) => setToUserId(e.target.value)}
            disabled={
              submitting ||
              loadingOptions ||
              !selectedProject ||
              currentOwners.length === 0 ||
              !fromUserId
            }
          >
            <option value="">Select new engineer…</option>
            {toOptions.map((c) => (
              <option key={c.user_id} value={c.user_id}>
                {c.email}
              </option>
            ))}
          </Form.Select>
          {selectedProject &&
            !loadingOptions &&
            candidates.length <= 1 && (
              <Form.Text className="text-muted">
                No other civil_team members are allocated to this project. Allocate
                the new engineer to the project first, then reassign.
              </Form.Text>
            )}
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={submitting}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSubmit} disabled={!canSubmit}>
          {submitting ? "Reassigning…" : "Confirm"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
